// ProOnboardingPage — Nouvel onboarding AYA Pro, écran par écran (MAQUETTE = vérité) (v5.25.0).
// Une question par écran · progression x/6 · adapté par métier (config ONBOARDING) ·
// sauvegarde auto (reprise exacte) · aperçu du profil · soumission /api/inscription-pro/submit.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Camera, Globe, ImagePlus, Link2, Mail, MapPin, Navigation, Phone, Search } from "lucide-react";
import { toast } from "sonner";
import { ACTIVITIES, ONBOARDING, COMMUNES, findActivity, normStr, SECTOR_KEY } from "@/components/pro/proActivities";
import { AyaProLogo } from "@/components/pro/AyaProLogo";
import { ProShareSheet } from "@/components/pro/ProShareCard";

const API = process.env.REACT_APP_BACKEND_URL;
const DRAFT_KEY = "aya_pro_onboarding_v1";
// activity → welcome → name → offers → location → contact → photo → preview → done
const FLOW = ["activity", "welcome", "name", "offers", "location", "contact", "photo", "preview", "done"];
const PROGRESS = { welcome: 1, name: 2, offers: 3, location: 4, contact: 5, photo: 6 };

const compressImage = (file, maxDim = 900, quality = 0.78) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const k = Math.min(1, maxDim / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    resolve(c.toDataURL("image/jpeg", quality));
  };
  img.onerror = () => resolve(null);
  img.src = URL.createObjectURL(file);
});

export default function ProOnboardingPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [step, setStep] = useState("activity");
  const [d, setD] = useState({ activityId: null, name: "", offerings: [], mobile: false, commune: "", quartier: "", address: "", gps: null, whatsapp: "", show_whatsapp: true, phone_fixe: "", email: "", website: "" });
  const [q, setQ] = useState("");
  const [oq, setOq] = useState("");
  const [busy, setBusy] = useState(false);
  const photoRef = useRef(null);
  const logoRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [logo, setLogo] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  // Reprise automatique de l'inscription (sauvegarde à chaque étape).
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (saved?.step && FLOW.includes(saved.step) && saved.step !== "done") {
        setD((p) => ({ ...p, ...saved.d }));
        setPhoto(saved.photo || null); setLogo(saved.logo || null);
        setStep(saved.step);
        toast.success("Reprise de votre inscription là où vous vous étiez arrêté");
        return;
      }
    } catch {}
    const pre = sp.get("activity");
    if (pre && findActivity(pre)) { setD((p) => ({ ...p, activityId: pre })); setStep("welcome"); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (step === "done") { localStorage.removeItem(DRAFT_KEY); return; }
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, d, photo, logo })); } catch {}
  }, [step, d, photo, logo]);

  const act = findActivity(d.activityId);
  const cfg = ONBOARDING[d.activityId] || ONBOARDING.autres;
  const go = (s) => { setStep(s); window.scrollTo(0, 0); };
  const back = () => {
    const i = FLOW.indexOf(step);
    if (i <= 0) { navigate(-1); return; }
    go(FLOW[step === "welcome" ? 0 : i - 1]);
  };

  const shownActs = useMemo(() => {
    const nq = normStr(q.trim());
    return nq ? ACTIVITIES.filter((a) => normStr(`${a.label} ${a.sub} ${a.keywords}`).includes(nq)) : ACTIVITIES;
  }, [q]);
  const shownOffers = useMemo(() => {
    const nq = normStr(oq.trim());
    const base = cfg.offerings;
    return nq ? base.filter((o) => normStr(o).includes(nq)) : base;
  }, [oq, cfg]);
  const toggleOffer = (o) => setD((p) => ({ ...p, offerings: p.offerings.includes(o) ? p.offerings.filter((x) => x !== o) : [...p.offerings, o] }));

  const locate = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non disponible"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setD((p) => ({ ...p, gps: { lat: pos.coords.latitude, lng: pos.coords.longitude } })); toast.success("Position enregistrée ✓"); },
      () => toast.error("Impossible d'obtenir votre position"),
      { timeout: 8000 },
    );
  };
  const pickFile = async (e, isLogo) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const data = await compressImage(f, isLogo ? 400 : 900);
    if (!data) { toast.error("Image illisible"); return; }
    if (isLogo) setLogo(data); else setPhoto(data);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/inscription-pro/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: d.name.trim(), category: d.activityId, commune: d.commune, phone: d.whatsapp.replace(/\D/g, ""),
          optional: { address: d.address || null, email: d.email || null, description: d.offerings.join(", ") || null },
          extra: { offerings: d.offerings, quartier: d.quartier || null, mobile: d.mobile, gps: d.gps, show_whatsapp: d.show_whatsapp, phone_fixe: d.phone_fixe || null, website: d.website || null, photo, logo },
        }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || "submit_failed");
      localStorage.setItem(SECTOR_KEY, d.activityId);
      go("done");
    } catch (e) {
      toast.error(e.message === "invalid_phone" ? "Numéro WhatsApp invalide" : "Échec de l'envoi — réessayez");
    } finally { setBusy(false); }
  };

  const prog = PROGRESS[step];
  const waOk = /^0?\d{9,10}$/.test(d.whatsapp.replace(/\D/g, ""));

  return (
    <div style={S.page} data-testid="pro-onboarding-page">
      <div style={S.wrap}>
        {/* Barre AYA + progression */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0 6px" }}>
          <button onClick={back} style={S.backBtn} data-testid="onb-back"><ArrowLeft size={14} /> Retour</button>
          <span style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}><AyaProLogo variant="onboarding" onLight /></span>
          <span style={{ width: 72, textAlign: "right", fontSize: 11.5, fontWeight: 800, color: "#6B7280" }} data-testid="onb-progress-label">{prog ? `${prog}/6` : ""}</span>
        </div>
        {prog && (
          <div style={{ height: 5, borderRadius: 99, background: "#E5E7EB", overflow: "hidden" }} data-testid="onb-progress-bar">
            <div style={{ width: `${(prog / 6) * 100}%`, height: "100%", borderRadius: 99, background: "#0E8A4F", transition: "width .35s ease" }} />
          </div>
        )}

        {/* ── 1. ACTIVITÉ ── */}
        {step === "activity" && (
          <div style={S.in} data-testid="onb-step-activity">
            <h1 style={S.h1}>Quelle activité souhaitez-vous inscrire ?</h1>
            <p style={S.sub}>Sélectionnez votre secteur pour commencer.</p>
            <div style={S.searchBox}>
              <Search size={14} color="#9CA3AF" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un métier…" style={S.searchInput} data-testid="onb-activity-search" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(104px, 1fr))", gap: 8, marginTop: 12 }}>
              {shownActs.map((a) => (
                <button key={a.id} onClick={() => { setD((p) => ({ ...p, activityId: a.id, offerings: [] })); go("welcome"); }} data-testid={`onb-activity-${a.id}`}
                  style={{ background: a.bg, border: d.activityId === a.id ? "2px solid #0E8A4F" : "2px solid transparent", borderRadius: 14, padding: "13px 8px", cursor: "pointer", textAlign: "center" }}>
                  <span style={{ fontSize: 26, display: "block" }}>{a.icon}</span>
                  <span style={{ fontSize: 10.5, fontWeight: 800, color: "#111827", lineHeight: 1.2, display: "block", marginTop: 6 }}>{a.label}</span>
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 9, alignItems: "center", background: "#FFF7E6", borderRadius: 12, padding: "10px 13px", marginTop: 14 }}>
              <span>💡</span><span style={{ fontSize: 11.5, color: "#6B7280" }}><b style={{ color: "#111827" }}>Vous ne trouvez pas votre activité ?</b> Choisissez « Autres activités ».</span>
            </div>
          </div>
        )}

        {/* ── 2. BIENVENUE ── */}
        {step === "welcome" && act && (
          <div style={{ ...S.in, textAlign: "center" }} data-testid="onb-step-welcome">
            <div style={{ ...S.illu, background: act.bg }}>{act.icon}</div>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, color: "#0E8A4F", marginTop: 14 }}>BIENVENUE DANS AYA PRO</div>
            <h1 style={{ ...S.h1, marginTop: 6 }} data-testid="onb-welcome-activity">{act.label}</h1>
            <p style={S.sub}>{act.sub}</p>
            <div style={{ textAlign: "left", display: "grid", gap: 9, margin: "16px auto 0", maxWidth: 330 }}>
              {["Soyez visible sur AYA", "Attirez de nouveaux clients", "Présentez vos produits et services", "Gérez votre activité facilement"].map((t) => (
                <div key={t} style={{ display: "flex", gap: 9, alignItems: "center", fontSize: 13, fontWeight: 700, color: "#111827" }}>
                  <span style={{ width: 20, height: 20, borderRadius: "50%", background: "#0E8A4F", color: "#fff", fontSize: 11, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✓</span>{t}
                </div>
              ))}
            </div>
            <button style={S.cta} onClick={() => go("name")} data-testid="onb-welcome-start">Commencer mon inscription <ArrowRight size={15} /></button>
            <div style={{ display: "flex", gap: 9, alignItems: "flex-start", background: "#F0FDF4", borderRadius: 12, padding: "11px 13px", marginTop: 12, textAlign: "left" }}>
              <span style={{ color: "#0E8A4F" }}>✓</span>
              <span style={{ fontSize: 11.5, color: "#6B7280" }}><b style={{ color: "#111827" }}>C'est gratuit pour commencer.</b> Vous pourrez activer les options payantes plus tard.</span>
            </div>
          </div>
        )}

        {/* ── 3. NOM ── */}
        {step === "name" && (
          <div style={{ ...S.in, textAlign: "center" }} data-testid="onb-step-name">
            <div style={{ ...S.illu, background: act?.bg || "#F1F2F4" }}>{act?.icon}</div>
            <h1 style={{ ...S.h1, marginTop: 16 }}>{cfg.nameQ}</h1>
            <p style={S.sub}>C'est le nom qui sera affiché sur AYA.</p>
            <div style={{ textAlign: "left", maxWidth: 380, margin: "16px auto 0" }}>
              <label style={S.label}>Nom {act?.id === "avocat" ? "du cabinet" : "de l'activité"} *</label>
              <input value={d.name} onChange={(e) => setD((p) => ({ ...p, name: e.target.value }))} placeholder={cfg.namePh} style={S.input} data-testid="onb-name-input" autoFocus />
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>{cfg.namePh}</div>
            </div>
            <button style={{ ...S.cta, opacity: d.name.trim().length >= 3 ? 1 : 0.45 }} disabled={d.name.trim().length < 3} onClick={() => go("offers")} data-testid="onb-name-next">Continuer <ArrowRight size={15} /></button>
          </div>
        )}

        {/* ── 4. QUE PROPOSEZ-VOUS ── */}
        {step === "offers" && (
          <div style={{ ...S.in, textAlign: "center" }} data-testid="onb-step-offers">
            <div style={{ ...S.illu, background: act?.bg || "#F1F2F4", width: 86, height: 86, fontSize: 38 }}>{act?.icon}</div>
            <h1 style={{ ...S.h1, marginTop: 14 }}>{cfg.offerQ}</h1>
            <p style={S.sub}>{cfg.offerSub}</p>
            <div style={{ ...S.searchBox, maxWidth: 380, margin: "14px auto 0" }}>
              <Search size={14} color="#9CA3AF" />
              <input value={oq} onChange={(e) => setOq(e.target.value)} placeholder="Rechercher un produit ou service…" style={S.searchInput} data-testid="onb-offers-search" />
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center", marginTop: 14 }} data-testid="onb-offers-chips">
              {shownOffers.map((o) => {
                const on = d.offerings.includes(o);
                return (
                  <button key={o} onClick={() => toggleOffer(o)} data-testid={`onb-offer-${o}`}
                    style={{ padding: "8px 14px", borderRadius: 999, cursor: "pointer", fontSize: 12, fontWeight: 700, border: on ? "1.5px solid #0E8A4F" : "1.5px solid #E5E7EB", background: on ? "#E8F8EF" : "#fff", color: on ? "#0E8A4F" : "#374151" }}>
                    {on ? "✓ " : ""}{o}
                  </button>
                );
              })}
              {oq.trim() && !shownOffers.some((o) => normStr(o) === normStr(oq.trim())) && (
                <button onClick={() => { toggleOffer(oq.trim()); setOq(""); }} data-testid="onb-offer-add-custom"
                  style={{ padding: "8px 14px", borderRadius: 999, cursor: "pointer", fontSize: 12, fontWeight: 800, border: "1.5px dashed #0E8A4F", background: "#fff", color: "#0E8A4F" }}>
                  + Ajouter « {oq.trim()} »
                </button>
              )}
            </div>
            <button style={{ ...S.cta, opacity: d.offerings.length ? 1 : 0.45 }} disabled={!d.offerings.length} onClick={() => go("location")} data-testid="onb-offers-next">Continuer <ArrowRight size={15} /></button>
          </div>
        )}

        {/* ── 5. LOCALISATION ── */}
        {step === "location" && (
          <div style={{ ...S.in, textAlign: "center" }} data-testid="onb-step-location">
            <div style={{ ...S.illu, width: 66, height: 66, fontSize: 0, background: "#0E8A4F" }}><MapPin size={30} color="#fff" /></div>
            <h1 style={{ ...S.h1, marginTop: 14 }}>Où se situe votre activité ?</h1>
            <p style={S.sub}>Cette adresse sera visible sur AYA pour que les clients puissent vous trouver.</p>
            <div style={{ display: "inline-flex", background: "#F3F4F6", borderRadius: 999, padding: 3, marginTop: 13 }}>
              {[["Adresse exacte", false], ["Je me déplace", true]].map(([l, v]) => (
                <button key={l} onClick={() => setD((p) => ({ ...p, mobile: v }))} data-testid={v ? "onb-loc-mobile" : "onb-loc-fixed"}
                  style={{ padding: "7px 15px", borderRadius: 999, border: "none", cursor: "pointer", fontSize: 11.5, fontWeight: 800, background: d.mobile === v ? "#fff" : "transparent", color: d.mobile === v ? "#0E8A4F" : "#6B7280", boxShadow: d.mobile === v ? "0 1px 4px rgba(0,0,0,0.08)" : "none" }}>{l}</button>
              ))}
            </div>
            <div style={{ textAlign: "left", maxWidth: 380, margin: "14px auto 0", display: "grid", gap: 11 }}>
              <div>
                <label style={S.label}>Commune *</label>
                <select value={d.commune} onChange={(e) => setD((p) => ({ ...p, commune: e.target.value }))} style={{ ...S.input, appearance: "auto" }} data-testid="onb-commune-select">
                  <option value="">Choisir…</option>
                  {COMMUNES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Quartier *</label>
                <input value={d.quartier} onChange={(e) => setD((p) => ({ ...p, quartier: e.target.value }))} placeholder="Ex: Riviera, Angré 8e tranche…" style={S.input} data-testid="onb-quartier-input" />
              </div>
              {!d.mobile && (
                <div>
                  <label style={S.label}>Adresse détaillée</label>
                  <input value={d.address} onChange={(e) => setD((p) => ({ ...p, address: e.target.value }))} placeholder="Ex: Boulevard François Mitterrand" style={S.input} data-testid="onb-address-input" />
                </div>
              )}
              <div style={{ borderRadius: 14, background: "#EDF5EF", border: "1px solid #DCEBE0", minHeight: 110, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <MapPin size={26} color="#0E8A4F" fill={d.gps ? "#0E8A4F" : "none"} />
                <button onClick={locate} data-testid="onb-locate-btn"
                  style={{ position: "absolute", bottom: 9, left: "50%", transform: "translateX(-50%)", display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 999, background: "#fff", border: "1px solid #E5E7EB", color: "#0E8A4F", fontSize: 11, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                  <Navigation size={12} /> {d.gps ? "Position enregistrée ✓" : "Utiliser ma position actuelle"}
                </button>
              </div>
            </div>
            <button style={{ ...S.cta, opacity: d.commune && d.quartier.trim() ? 1 : 0.45 }} disabled={!d.commune || !d.quartier.trim()} onClick={() => go("contact")} data-testid="onb-location-next">Continuer <ArrowRight size={15} /></button>
          </div>
        )}

        {/* ── 6. CONTACT ── */}
        {step === "contact" && (
          <div style={{ ...S.in, textAlign: "center" }} data-testid="onb-step-contact">
            <div style={{ ...S.illu, width: 66, height: 66, fontSize: 0, background: "#0E8A4F" }}><Phone size={28} color="#fff" /></div>
            <h1 style={{ ...S.h1, marginTop: 14 }}>Comment vos clients peuvent vous contacter ?</h1>
            <p style={S.sub}>Ajoutez au moins un moyen de contact.</p>
            <div style={{ textAlign: "left", maxWidth: 380, margin: "15px auto 0", display: "grid", gap: 11 }}>
              <div>
                <label style={S.label}>Téléphone WhatsApp *</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ ...S.input, width: 64, textAlign: "center", flexShrink: 0, color: "#374151", fontWeight: 800 }}>+225</span>
                  <input value={d.whatsapp} onChange={(e) => setD((p) => ({ ...p, whatsapp: e.target.value }))} placeholder="07 08 90 12 34" inputMode="tel" style={{ ...S.input, flex: 1 }} data-testid="onb-whatsapp-input" />
                  <span style={{ width: 36, height: 36, borderRadius: "50%", background: "#25D366", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15, flexShrink: 0 }}>✆</span>
                </div>
              </div>
              <label style={{ display: "flex", gap: 9, alignItems: "center", fontSize: 12, fontWeight: 700, color: "#0E8A4F", cursor: "pointer" }}>
                <input type="checkbox" checked={d.show_whatsapp} onChange={(e) => setD((p) => ({ ...p, show_whatsapp: e.target.checked }))} data-testid="onb-show-whatsapp" style={{ accentColor: "#0E8A4F", width: 16, height: 16 }} />
                Afficher mon WhatsApp sur mon profil
              </label>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: "#6B7280", marginTop: 3 }}>Autres contacts (optionnel)</div>
              {[["phone_fixe", Phone, "Téléphone fixe"], ["email", Mail, "E-mail"], ["website", Globe, "Site web ou réseaux sociaux"]].map(([k, Ic, ph]) => (
                <div key={k} style={{ display: "flex", gap: 9, alignItems: "center", border: "1px solid #E5E7EB", borderRadius: 12, padding: "3px 12px", background: "#fff" }}>
                  <Ic size={14} color="#6B7280" style={{ flexShrink: 0 }} />
                  <input value={d[k]} onChange={(e) => setD((p) => ({ ...p, [k]: e.target.value }))} placeholder={ph} style={{ border: "none", outline: "none", fontSize: 12.5, padding: "10px 0", width: "100%", background: "none" }} data-testid={`onb-${k}-input`} />
                  <Link2 size={13} color="#D1D5DB" />
                </div>
              ))}
            </div>
            <button style={{ ...S.cta, opacity: waOk ? 1 : 0.45 }} disabled={!waOk} onClick={() => go("photo")} data-testid="onb-contact-next">Continuer <ArrowRight size={15} /></button>
          </div>
        )}

        {/* ── 7. PHOTO & LOGO ── */}
        {step === "photo" && (
          <div style={{ ...S.in, textAlign: "center" }} data-testid="onb-step-photo">
            <div style={{ ...S.illu, width: 66, height: 66, fontSize: 0, background: "#0E8A4F" }}><Camera size={27} color="#fff" /></div>
            <h1 style={{ ...S.h1, marginTop: 14 }}>Ajoutez une photo de votre activité</h1>
            <p style={S.sub}>Une belle photo aide vos clients à vous reconnaître.</p>
            <input ref={photoRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => pickFile(e, false)} data-testid="onb-photo-file" />
            <input ref={logoRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => pickFile(e, true)} data-testid="onb-logo-file" />
            <div onClick={() => photoRef.current?.click()} role="button" data-testid="onb-photo-drop"
              style={{ maxWidth: 380, margin: "15px auto 0", borderRadius: 16, overflow: "hidden", border: photo ? "none" : "2px dashed #CBD5D1", background: "#F7FAF8", cursor: "pointer", position: "relative", minHeight: 170, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {photo ? (
                <>
                  <img src={photo} alt="Votre activité" style={{ width: "100%", height: 200, objectFit: "cover", display: "block" }} />
                  <span style={{ position: "absolute", bottom: 10, left: "50%", transform: "translateX(-50%)", display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 999, background: "rgba(17,24,39,0.75)", color: "#fff", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}><Camera size={12} /> Changer la photo</span>
                </>
              ) : (
                <div style={{ padding: 22 }}>
                  <ImagePlus size={30} color="#9CA3AF" />
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: "#374151", marginTop: 8 }}>📷 Prendre ou choisir une photo</div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 3 }}>JPG ou PNG — compressée automatiquement</div>
                </div>
              )}
            </div>
            <div onClick={() => logoRef.current?.click()} role="button" data-testid="onb-logo-drop"
              style={{ maxWidth: 380, margin: "11px auto 0", display: "flex", gap: 10, alignItems: "center", border: "1px solid #E5E7EB", borderRadius: 13, padding: "11px 13px", background: "#fff", cursor: "pointer", textAlign: "left" }}>
              {logo ? <img src={logo} alt="logo" style={{ width: 36, height: 36, borderRadius: 9, objectFit: "cover" }} />
                : <span style={{ width: 36, height: 36, borderRadius: 9, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>🖼️</span>}
              <span style={{ fontSize: 12.5, fontWeight: 700, color: "#374151" }}>{logo ? "Changer le logo" : "Ajouter un logo"} <span style={{ color: "#9CA3AF", fontWeight: 600 }}>(optionnel)</span></span>
            </div>
            <button style={S.cta} onClick={() => go("preview")} data-testid="onb-photo-next">{photo ? "Continuer" : "Continuer sans photo"} <ArrowRight size={15} /></button>
          </div>
        )}

        {/* ── 8. APERÇU ── */}
        {step === "preview" && act && (
          <div style={S.in} data-testid="onb-step-preview">
            <h1 style={{ ...S.h1, textAlign: "center" }}>Aperçu de votre profil</h1>
            <p style={{ ...S.sub, textAlign: "center" }}>Voici comment votre activité apparaîtra sur AYA.</p>
            <div style={{ maxWidth: 400, margin: "15px auto 0", border: "1px solid #E5E7EB", borderRadius: 16, overflow: "hidden", background: "#fff", boxShadow: "0 2px 10px rgba(17,24,39,0.05)" }} data-testid="onb-preview-card">
              <div style={{ display: "flex", gap: 12, padding: 13 }}>
                {photo ? <img src={photo} alt="" style={{ width: 74, height: 74, borderRadius: 12, objectFit: "cover", flexShrink: 0 }} />
                  : <span style={{ width: 74, height: 74, borderRadius: 12, background: act.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, flexShrink: 0 }}>{act.icon}</span>}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: "#111827" }}>{d.name}</div>
                  <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 2 }}>{act.label}{d.offerings[0] ? ` · ${d.offerings[0]}` : ""}</div>
                  <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 2 }}>📍 {d.commune}{d.quartier ? `, ${d.quartier}` : ""}</div>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 6, padding: "3px 10px", borderRadius: 999, background: "#E8F8EF", color: "#0E8A4F", fontSize: 10.5, fontWeight: 800 }}>● Ouvert maintenant</span>
                </div>
              </div>
              <div style={{ display: "flex", borderTop: "1px solid #F3F4F6" }}>
                {[["📞", "Appeler"], ["💬", "WhatsApp"], ["📍", "Itinéraire"]].map(([i, l]) => (
                  <span key={l} style={{ flex: 1, textAlign: "center", padding: "10px 0", fontSize: 11, fontWeight: 800, color: "#0E8A4F", borderRight: l !== "Itinéraire" ? "1px solid #F3F4F6" : "none" }}>{i} {l}</span>
                ))}
              </div>
            </div>
            <div style={{ maxWidth: 400, margin: "13px auto 0", display: "grid", gap: 7 }}>
              {[["Activité", act.label, "activity"], ["Nom", d.name, "name"], ["Services", d.offerings.join(", "), "offers"], ["Localisation", `${d.commune}${d.quartier ? `, ${d.quartier}` : ""}`, "location"], ["WhatsApp", `+225 ${d.whatsapp}`, "contact"], ["Photo", photo ? "Ajoutée ✓" : "Aucune", "photo"]].map(([l, v, s]) => (
                <div key={l} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, padding: "8px 11px", borderRadius: 11, background: "#F8FAF9" }}>
                  <span style={{ color: "#6B7280", fontWeight: 700, width: 86, flexShrink: 0 }}>{l}</span>
                  <span style={{ color: "#111827", fontWeight: 700, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</span>
                  <button onClick={() => go(s)} style={{ background: "none", border: "none", color: "#0E8A4F", fontSize: 11.5, fontWeight: 800, cursor: "pointer", padding: 0 }} data-testid={`onb-edit-${s}`}>Modifier</button>
                </div>
              ))}
            </div>
            <button style={{ ...S.cta, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={submit} data-testid="onb-submit">{busy ? "Création en cours…" : "Créer mon espace Pro"} <ArrowRight size={15} /></button>
          </div>
        )}

        {/* ── 9. FINALISATION ── */}
        {step === "done" && act && (
          <div style={{ ...S.in, textAlign: "center" }} data-testid="onb-step-done">
            <div style={{ fontSize: 13 }}>🎉 ✨ 🎊</div>
            <div style={{ width: 84, height: 84, borderRadius: "50%", background: "#0E8A4F", color: "#fff", fontSize: 38, display: "inline-flex", alignItems: "center", justifyContent: "center", marginTop: 6, boxShadow: "0 8px 24px rgba(14,138,79,0.3)" }}>✓</div>
            <h1 style={{ ...S.h1, marginTop: 14 }}>Inscription réussie !</h1>
            <p style={S.sub}>Votre espace professionnel est prêt.</p>
            <div style={{ maxWidth: 380, margin: "15px auto 0", display: "flex", gap: 11, alignItems: "center", border: "1px solid #E5E7EB", borderRadius: 14, padding: 12, background: "#fff", textAlign: "left" }}>
              {photo ? <img src={photo} alt="" style={{ width: 52, height: 52, borderRadius: 11, objectFit: "cover", flexShrink: 0 }} />
                : <span style={{ width: 52, height: 52, borderRadius: 11, background: act.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>{act.icon}</span>}
              <div>
                <div style={{ fontSize: 14, fontWeight: 900, color: "#111827" }}>{d.name}</div>
                <div style={{ fontSize: 11.5, color: "#6B7280" }}>📍 {d.commune}{d.quartier ? `, ${d.quartier}` : ""}</div>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 4, padding: "2px 9px", borderRadius: 999, background: "#FEF3C7", color: "#B45309", fontSize: 10, fontWeight: 800 }} data-testid="onb-pending-badge">● En cours de validation</span>
              </div>
            </div>
            <div style={{ maxWidth: 380, margin: "12px auto 0", display: "grid", gap: 8, textAlign: "left" }}>
              {["Votre profil est en cours de validation par notre équipe (généralement moins de 24h).", "Vous recevrez une notification dès qu'il sera en ligne sur AYA.", "Vous pourrez ensuite accéder à votre tableau de bord pour gérer votre activité."].map((t, i) => (
                <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 11.5, color: "#374151", background: "#F0FDF4", borderRadius: 11, padding: "9px 12px" }}>
                  <span style={{ color: "#0E8A4F", flexShrink: 0 }}>🛡</span>{t}
                </div>
              ))}
            </div>
            <button style={{ ...S.cta, background: "#25D366" }} onClick={() => setShareOpen(true)} data-testid="onb-done-share">📣 Annoncer mon espace sur WhatsApp</button>
            <button style={{ ...S.cta, background: "#F97316", marginTop: 9 }} onClick={() => navigate("/pro/acces")} data-testid="onb-done-pro">Voir mon espace Pro</button>
            <button style={{ ...S.cta, background: "#fff", color: "#0E8A4F", border: "1.5px solid #0E8A4F", marginTop: 9 }} onClick={() => navigate("/search")} data-testid="onb-done-home">Retour à l'accueil</button>
            {shareOpen && (
              <ProShareSheet onClose={() => setShareOpen(false)}
                data={{ name: d.name, activityLabel: act.label, icon: act.icon, bg: act.bg, photo, offerings: d.offerings, commune: d.commune, quartier: d.quartier }} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  page: { minHeight: "100vh", background: "#FBFDFB", fontFamily: "'Manrope', system-ui, sans-serif", paddingBottom: 40 },
  wrap: { maxWidth: 560, margin: "0 auto", padding: "0 18px", boxSizing: "border-box" },
  in: { animation: "ayaProIn .3s ease both", paddingTop: 18 },
  h1: { margin: 0, fontSize: 21, fontWeight: 900, color: "#111827", lineHeight: 1.25, letterSpacing: -0.2 },
  sub: { margin: "6px 0 0", fontSize: 12.5, color: "#6B7280", lineHeight: 1.45 },
  illu: { width: 110, height: 110, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 48, marginTop: 10 },
  searchBox: { display: "flex", alignItems: "center", gap: 8, padding: "10px 13px", borderRadius: 999, border: "1px solid #E5E7EB", background: "#fff", marginTop: 13 },
  searchInput: { border: "none", outline: "none", background: "none", fontSize: 12.5, color: "#111827", width: "100%" },
  label: { display: "block", fontSize: 11.5, fontWeight: 800, color: "#374151", marginBottom: 5 },
  input: { width: "100%", boxSizing: "border-box", padding: "11px 13px", borderRadius: 12, border: "1.5px solid #E5E7EB", background: "#fff", fontSize: 13.5, color: "#111827", outline: "none", fontFamily: "inherit" },
  backBtn: { display: "inline-flex", alignItems: "center", gap: 5, background: "none", border: "none", color: "#111827", fontSize: 13, fontWeight: 700, cursor: "pointer", padding: 0, width: 72 },
  cta: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", maxWidth: 380, margin: "20px auto 0", padding: "14px 18px", borderRadius: 13, border: "none", background: "#0E8A4F", color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer" },
};
